/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 24th 2023, 8:17:11 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useTranslatedText } from "core";
import { DesignLayout } from "lib/schemas";
import { HTMLEditor, useToggleState, AlertSnackBar } from "ui-widgets";
import { AutoSaveFormProvider } from "ui-widgets/forms/providers";
import { useDesignLayoutAPI } from "service-builder/services";

interface CustomEditorProps {
  designLayout: DesignLayout;
  onChangeValue: ReturnType<
    ReturnType<typeof useDesignLayoutAPI>["useDesignLayoutAPIForm"]
  >["useDesignLayoutAPIFormReturn"]["useFormReturn"]["setValue"];
}

interface DesignLayoutEditorProps
  extends Pick<React.ComponentProps<typeof HTMLEditor>, "appendContents" | "setContents" | "minHeight"> {
  layoutId: DesignLayout["id"];
  onSaveDesignLayout: (designLayout: DesignLayout) => void;
  newDesignLayoutDefault: DesignLayout;
  editor?: "HTML" | "JSON" | "PLAIN" | "CUSTOM";
  autoSaveNewLayout?: boolean;
  CustomEditorComponent?: React.ComponentType<CustomEditorProps>;
}

export const DesignLayoutEditor = ({
  layoutId,
  newDesignLayoutDefault,
  onSaveDesignLayout,
  appendContents,
  minHeight,
  setContents,
  editor = "HTML",
  autoSaveNewLayout = false,
  CustomEditorComponent = () => null,
}: DesignLayoutEditorProps) => {
  const LANG = useTranslatedText();

  const { useDesignLayoutAPIForm } = useDesignLayoutAPI();

  const { useDesignLayoutAPIFormReturn } = useDesignLayoutAPIForm(layoutId, onSaveDesignLayout, newDesignLayoutDefault);

  const onChangeHTMLContent = (html_design: string) =>
    useDesignLayoutAPIFormReturn.useFormReturn.setValue("html_design", html_design, { shouldDirty: true });

  const onChangeLayoutCSS = (css: string) =>
    useDesignLayoutAPIFormReturn.useFormReturn.setValue("css", css, { shouldDirty: true });

  const htmlDesign = useDesignLayoutAPIFormReturn.useFormReturn.watch("html_design");
  const css = useDesignLayoutAPIFormReturn.useFormReturn.watch("css");
  const errors = useDesignLayoutAPIFormReturn.useFormReturn.formState?.errors;

  const isInitialDesignLayoutFetch =
    useDesignLayoutAPIFormReturn.isAPIFetchingEditItem && !useDesignLayoutAPIFormReturn.isSuccessfulAPIDataSend;

  // Only show the editor if its a new design layout or the html design for an existing design layout
  // has been loaded from the API.
  const showEditor = !layoutId || (layoutId && !isInitialDesignLayoutFetch && ![null, undefined].includes(htmlDesign));

  let editorComponent: React.ReactNode = null;

  switch (editor) {
    case "HTML":
      editorComponent = (
        <HTMLEditor
          defaultValue={htmlDesign}
          appendContents={appendContents}
          setContents={setContents}
          onChangeHTMLContent={onChangeHTMLContent}
          minHeight={minHeight}
          css={css}
          cssErrorMessage={errors?.css?.message}
          onChangeLayoutCSS={onChangeLayoutCSS}
        />
      );
      break;
    case "PLAIN":
      editorComponent = (
        <TextField value={htmlDesign} onChange={(e) => onChangeHTMLContent(e.target.value)} multiline rows={20} />
      );
      break;
    case "CUSTOM":
      editorComponent = (
        <CustomEditorComponent
          designLayout={useDesignLayoutAPIFormReturn.useFormReturn.watch()}
          onChangeValue={useDesignLayoutAPIFormReturn.useFormReturn.setValue}
        />
      );
      break;
  }

  const loaderComponent = <Skeleton animation="wave" variant="rectangular" height={500} />;

  const {
    handleClose: closeUpdateNotification,
    handleOpen: openUpdateNotification,
    open: updateNotificationOpenState,
  } = useToggleState();

  React.useEffect(() => {
    if (useDesignLayoutAPIFormReturn.isSuccessfulAPIDataSend) {
      openUpdateNotification();
    }
  }, [useDesignLayoutAPIFormReturn.isSuccessfulAPIDataSend, openUpdateNotification]);

  return (
    <>
      <AlertSnackBar
        open={updateNotificationOpenState}
        message={`${LANG.savedSuccessfully}!`}
        severity="info"
        vertical={"top"}
        horizontal={"center"}
        handleClose={closeUpdateNotification}
        canBeClosedByUser={false}
        autoHideDuration={1200}
      />

      <AutoSaveFormProvider
        isNewData={autoSaveNewLayout && !layoutId} // We dont want to auto save until the user has made a change.
        submitFunction={useDesignLayoutAPIFormReturn.submitAPIForm}
        useFormReturn={useDesignLayoutAPIFormReturn.useFormReturn}
      >
        {showEditor ? <Stack spacing={2}>{editorComponent}</Stack> : loaderComponent}
      </AutoSaveFormProvider>
    </>
  );
};
